import React from "react";
import "./default.css";
import { createPortal } from "react-dom";

/**
 * @typedef TranslationFieldPreviewState
 * @property {boolean} isOpen
 * @property {string} html
 * @property {(html: string) => void} setHtml
 * @property {string} css
 * @property {(css: string) => void} setCss
 * @property {(html: string) => void} open
 * @property {() => void} close
 */

/** @type {Window | null} */
let externalPreviewWindow = null;

export function useTranslationFieldPreview()
{
    const [isOpen, setIsOpen] = React.useState(false);
    const [html, setHtml] = React.useState('');
    const [css, setCss] = React.useState('');
    /** @type {React.MutableRefObject<NodeJS.Timeout | undefined>} */
    const debounceHtmlRef = React.useRef();
    /** @type {React.MutableRefObject<NodeJS.Timeout | undefined>} */
    const debounceCssRef = React.useRef();
    const updateExternalPreviewWindow = (/** @type {string|undefined} */ value, useDebounce = true) =>
    {
        const win = externalPreviewWindow;
        if (win && !win.closed)
        {
            if (useDebounce)
            {
                clearTimeout(debounceHtmlRef.current);
                debounceHtmlRef.current = setTimeout(() => { win.document.body.innerHTML = value || ''; }, 400);
            }
            else
            {
                win.document.body.innerHTML = value || '';
            }
        }
    };
    const setHtmlAndUpdate = (/** @type {string|undefined} */ value, useDebounce = true) =>
    {
        setHtml(value ?? '');
        updateExternalPreviewWindow(value ?? '', useDebounce);
    };
    /** @type {(win: Window, value: string) => void} */
    const setPreviewWindowCss = (win, value) =>
    {
        const styleElement = win.document.getElementById('custom-styles');
        if (styleElement)
        {
            styleElement.innerHTML = value;
        }
        else
        {
            const style = win.document.createElement('style');
            style.id = 'custom-styles';
            style.innerHTML = value;
            win.document.head.appendChild(style);
        }
    };
    const setCssAndUpdate = (/** @type {string} */ value, useDebounce = true) =>
    {
        setCss(value);
        const win = externalPreviewWindow;
        if (win && !win.closed)
        {
            if (useDebounce)
            {
                clearTimeout(debounceCssRef.current);
                debounceCssRef.current = setTimeout(() => { setPreviewWindowCss(win, value); }, 400);
            }
            else
            {
                setPreviewWindowCss(win, value);
            }
        }
    };
    const open = (/** @type {string|undefined} */ value) =>
    {
        setHtml(value ?? '');
        const win = externalPreviewWindow;
        if (win && !win.closed)
        {
            win.focus();
            win.document.body.innerHTML = value || '';
        }
        else
        {
            setIsOpen(true);
        }
    };
    const close = () =>
    {
        setIsOpen(false);
    };
    return { isOpen, html, setHtml: setHtmlAndUpdate, css, setCss: setCssAndUpdate, open, close };
}

/**
 * @typedef TranslationFieldPreviewProperties
 * @property {string} html
 * @property {() => void} onClose
 * @property {() => void} onOpenInNewWindow
 */

/**
 * @param {{ state: TranslationFieldPreviewState }} param0
 */
export default function TranslationFieldPreview({ state })
{
    /** @type {React.MutableRefObject<HTMLDivElement | null>} */
    const previewHostRef = React.useRef(null);
    // /** @type {React.MutableRefObject<ShadowRoot | null>} */
    // const shadowRef = React.useRef(null);
    /** @type {React.MutableRefObject<HTMLElement | null>} */
    const userHtmlRef = React.useRef(null);
    /** @type {React.MutableRefObject<HTMLElement | null>} */
    const userStyleRef = React.useRef(null);

    React.useLayoutEffect(() =>
    {
        if (!state.isOpen) { return; }
        const host = previewHostRef.current;
        if (!host) { return; }

        if (host.attachShadow)
        {
            const shadow = host.attachShadow({ mode: 'open' });
            const userHtmlElement = document.createElement('div');
            shadow.appendChild(userHtmlElement);
            const userStyleElement = document.createElement('style');
            shadow.appendChild(userStyleElement);
            userHtmlRef.current = userHtmlElement;
            userStyleRef.current = userStyleElement;
        }
        else
        {
            userHtmlRef.current = host;
        }
    },
    [state.isOpen]);

    React.useEffect(() =>
    {
        if (!state.isOpen) { return; }
        const userHtmlElement = userHtmlRef.current;
        const userStyleElement = userStyleRef.current;
        if (userHtmlElement)
        {
            userHtmlElement.innerHTML = state.html || '';
        }
        if (userStyleElement)
        {
            userStyleElement.innerHTML = state.css || '';
        }
    },
    [state.css, state.html, state.isOpen]);

    if (!state.isOpen) { return null; }

    const onClose = () => { state.close(); };
    const onOpenInNewWindow = () =>
    {
        const win = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        if (!win) { return; }
        win.document.write(`
            <html>
                <head>
                    <title>Podgląd treści</title>
                    <style class="custom-styles">${state.css}</style>
                </head>
                <body>${state.html}</body>
            </html>
        `);
        win.document.close();
        externalPreviewWindow = win;
        state.close();
    };
    const element =
    <div
        className="modal fade show translation-preview"
        role="dialog"
        style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
    >
        <div className="modal-dialog modal-lg" role="document" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title mr-2">Preview</h5>
                    <button type="button" className="close m-0 p-0" onClick={onOpenInNewWindow}>
                        <i className="fa fa-external-link-alt" aria-hidden="true" />
                    </button>
                    <button type="button" className="close" onClick={onClose}>
                        <span>&times;</span>
                    </button>
                </div>
                <div className="modal-body">
                    {/* <div dangerouslySetInnerHTML={{ __html: state.html }} /> */}
                    <div ref={previewHostRef} className="preview-host" />
                </div>
            </div>
        </div>
    </div>;
    return createPortal(element, document.body);
}